import { TranslateService } from '@ngx-translate/core';
import { tap, finalize, catchError, throwError } from 'rxjs';
import { BrandInterface, BrandApiService, BrandUpdatePayloadInterface } from '@/app/features/brand';

import { signal, Injectable, WritableSignal } from '@angular/core';
import { FormGroup, Validators, FormBuilder, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';

import { AlertService } from '@/app/core/services/alert/alert.service';
import { ValidationService } from '@/app/shared/services/validation.service';

import { CustomizeValues } from '@/app/shared/components/ui/customize-form/customize-form.component';

import { showBadRequestError } from '@/app/shared/helpers/errors.helper';

const DEFAULT_CUSTOMIZE_VALUES: CustomizeValues = {
  text_color: '#FFFFFF',
  button_color: '#18579A',
  button_text_color: '#FFFFFF',
  button_border_color: '#18579A',
  social_links_bgc: '#001124',
  social_links_color: '#FFFFFF',
  bg_type: 'solid',
  bg_color_g1: '#001124',
  bg_color_g2: '#001124',
  v_card_bg: '#18579A',
};

@Injectable({
  providedIn: 'root',
})
export class AdminBrandStore {
  public form: FormGroup;

  public isLoading: WritableSignal<boolean> = signal(false);

  public brand: WritableSignal<BrandInterface | undefined> = signal(undefined);

  public customizeValues: WritableSignal<CustomizeValues | undefined> = signal(undefined);

  constructor(
    private apiService: BrandApiService,
    private readonly formBuilder: FormBuilder,
    private readonly translateService: TranslateService,
    private readonly alertService: AlertService,
    private readonly validationService: ValidationService,
  ) {
    this.form = this.formBuilder.group(
      {
        name: [null, [Validators.required]],
        link: [null],
        title: [null],
        logo: [null],
        auth_logo: [null],
        favicon: [null],
        text_color: [null],
        zapier_enabled: [false],
        zapier_url: [null],
      },
      {
        validators: [this.zapierUrlRequiredIfEnabledValidator()],
      },
    );
  }

  public resetStyles() {
    this.customizeValues.set(DEFAULT_CUSTOMIZE_VALUES);
  }

  private zapierUrlRequiredIfEnabledValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const enabled = group.get('zapier_enabled')?.value;
      const url = group.get('zapier_url')?.value;

      if (enabled && (!url || url.trim() === '')) {
        return { zapierUrlRequired: true };
      }

      return null;
    };
  }

  public loadBrand() {
    this.isLoading.set(true);

    return this.apiService.loadAdminBrand().pipe(
      tap((response) => {
        this.brand.set(response.payload.content);

        if (Object.keys(response.payload.content.styles).length > 0) {
          this.customizeValues.set({
            ...this.customizeValues(),
            ...response.payload.content.styles,
          });
        } else {
          this.customizeValues.set(DEFAULT_CUSTOMIZE_VALUES);
        }

        this.form.patchValue({
          name: this.brand()?.name ?? '',
          title: this.brand()?.title ?? '',
          link: this.brand()?.link ?? '',
          logo: this.brand()?.logo_url ?? '',
          auth_logo: this.brand()?.auth_logo_url ?? undefined,
          favicon: this.brand()?.favicon_url ?? undefined,
          zapier_enabled: this.brand()?.zapier_enabled ?? null,
          zapier_url: this.brand()?.zapier_url ?? null,
        });
      }),

      finalize(() => this.isLoading.set(false)),
    );
  }

  public updateBrand() {
    this.isLoading.set(true);

    const payload: BrandUpdatePayloadInterface = this.form.value;

    payload['zapier_enabled'] = payload['zapier_enabled'] ? 1 : 0;

    ['logo', 'auth_logo', 'favicon'].forEach((imageKey) => {
      const control = this.form.get(imageKey);

      if (
        undefined === payload[imageKey as keyof Pick<BrandUpdatePayloadInterface, 'logo' | 'auth_logo' | 'favicon'>] ||
        !control?.dirty
      ) {
        delete payload[imageKey as keyof BrandUpdatePayloadInterface];
      }

      if (
        (imageKey === 'logo' || imageKey === 'auth_logo' || imageKey === 'favicon') &&
        payload[imageKey as keyof Pick<BrandUpdatePayloadInterface, 'logo' | 'auth_logo' | 'favicon'>] === null
      ) {
        payload[imageKey] = '';
      }
    });

    if (!!this.customizeValues()) {
      payload['styles'] = this.customizeValues()!;
    }

    return this.apiService.updateBrand(payload as BrandUpdatePayloadInterface).pipe(
      tap((response) => {
        this.brand.set(response.payload.content);

        this.alertService.success(this.translateService.instant('branding.form.submit.alert.success'));
      }),

      catchError((error) => {
        showBadRequestError(error, this.alertService);

        setTimeout(() => {
          this.validationService.setServerErrors(this.form, error);
        }, 10);

        return throwError(() => error);
      }),

      finalize(() => this.isLoading.set(false)),
    );
  }
}
